import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  TableProps,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import { ValidateRecoveryResponseChangeItem } from '@services/_api';
import { confirmRecovery } from '@services/cstool/recovery-account';

import { useRecoveryAccountContext } from './context';

import './recovery-account.scss';

const { Title } = Typography;

export default function RecoveryAccount() {
  const { handleValidateRecovery, environment } = useRecoveryAccountContext();
  const [form] = Form.useForm();

  const handleConfirmRecovery = async (change: ValidateRecoveryResponseChangeItem[]) => {
    const response = await confirmRecovery({
      currentUserId: form.getFieldsValue().currentUserId,
      targetUserId: form.getFieldsValue().targetUserId,
      updateAccounts: form.getFieldsValue().updateAccounts ?? [],
      environment: environment ?? '',
    });
    if (response.ok) {
      Modal.success({
        className: 'change-log-modal',
        title: 'Recovery successful',
        content: (
          <>
            <Row className="mb-4">
              <Alert message="Recovery successful" type="success" className="w-full" />
            </Row>
            <ChangeTable change={change ?? []} />
          </>
        ),
        centered: true,
      });
    } else {
      Modal.error({
        title: 'Recovery failed',
        content: (
          <>
            <Row>
              <Alert message="Recovery failed" type="error" className="w-full" />
            </Row>
          </>
        ),
      });
    }
  };

  const onFinish = async () => {
    const response = await handleValidateRecovery({
      ...form.getFieldsValue(),
      updateAccounts: form.getFieldsValue().updateAccounts ?? [],
      environment,
    });
    Modal.confirm({
      className: 'change-log-modal',
      bodyStyle: { width: 'fit-content' },
      title: response?.isValid ? 'Confirm Recovery' : 'Recovery failed',
      content: (
        <>
          <Row className="mb-4">
            <Alert
              message={response?.message}
              type={response?.isValid ? 'success' : 'error'}
              className="w-full"
            />
          </Row>
          {response && (
            <Row>
              <ChangeTable change={response?.change ?? []} />
            </Row>
          )}
        </>
      ),
      okText: response?.isValid ? 'Confirm Recovery' : 'Close',
      cancelText: 'Close',
      onOk: () => response?.isValid && handleConfirmRecovery(response?.change ?? []),
      centered: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Title level={5} className="mr-2">
        Recovery Account
      </Title>
      <Row gutter={[16, 16]}>
        <Col span={16}>
          <Form form={form} layout="vertical" onFinish={onFinish} validateTrigger="submit">
            <Form.Item
              name="currentUserId"
              label="Current User ID"
              required
              rules={[{ required: true, message: 'Please enter current user ID' }]}
            >
              <Input placeholder="Enter current user ID" />
            </Form.Item>
            <Form.Item
              name="targetUserId"
              label="Target User ID"
              required
              rules={[{ required: true, message: 'Please enter target user ID' }]}
            >
              <Input placeholder="Enter target user ID" />
            </Form.Item>
            <Form.List name="updateAccounts">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row gutter={16} key={field.key}>
                      <Col span={10}>
                        <Form.Item name={[field.name, 'type']} label="Type" required>
                          <Select placeholder="Select type">
                            <Select.Option value="ai">Apple</Select.Option>
                            <Select.Option value="st">Steam</Select.Option>
                            <Select.Option value="ga">Google</Select.Option>
                            <Select.Option value="portal">Portal</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item name={[field.name, 'account']} label="Account" required>
                          <Input placeholder="Enter account" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="Remove">
                          <Button
                            type="dashed"
                            onClick={() => remove(field.name)}
                            icon={<DeleteOutlined />}
                          >
                            Remove
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                  <Row className="mb-4">
                    <Button type="dashed" onClick={() => add({ type: '', account: '' })}>
                      Add Account
                    </Button>
                  </Row>
                </>
              )}
            </Form.List>
            <Button type="primary" htmlType="submit" disabled={!environment}>
              Recovery
            </Button>
            <Button
              className="ml-2"
              type="default"
              danger
              htmlType="button"
              onClick={() => form.resetFields()}
            >
              Clear Input
            </Button>
          </Form>
        </Col>
      </Row>
    </div>
  );
}

function ChangeTable({ change }: { change: ValidateRecoveryResponseChangeItem[] }) {
  const columns = useMemo<TableProps<ValidateRecoveryResponseChangeItem>['columns']>(
    () => [
      {
        title: 'Is Valid',
        dataIndex: 'isValid',
        key: 'isValid',
        render: (isValid) =>
          isValid ? <Tag color="green">Valid</Tag> : <Tag color="red">Invalid</Tag>,
      },
      {
        title: 'Account Type',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Account',
        dataIndex: 'account',
        key: 'account',
      },
      {
        title: 'Change',
        key: 'change',
        render: (_, record) => <>{`${record.oldUserId} -> ${record.userId}`}</>,
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
      },
    ],
    [],
  );
  return (
    <Table
      onRow={(data) => ({
        color: data.isValid ? 'green' : 'red',
      })}
      dataSource={change}
      columns={columns}
      pagination={false}
      rowClassName="w-full"
    />
  );
}
